import { friendshipAcceptancePath, friendshipDeclinePath, friendshipPath, friendshipsPath } from "@travily/routes";

export type FriendshipMutation = "create" | "accept" | "decline" | "unfriend";

interface SubmitArgs {
  mutation: FriendshipMutation;
  handle: string;
  friendshipId: number | null;
  csrfToken: string;
}

interface Request {
  url: string;
  method: "POST" | "DELETE";
  body?: Record<string, unknown>;
}

/**
 * Fires a friendship mutation against the existing Inertia endpoints with a raw
 * fetch (not the Inertia router) so the call never navigates — the friends-list
 * modal stays open and reconciles its own optimistic row state. The endpoints
 * `redirect_back` on success; `redirect: "manual"` turns that 3xx into an opaque
 * response we read as success without downloading the redirect target.
 */
export async function submitFriendshipMutation({
  mutation,
  handle,
  friendshipId,
  csrfToken,
}: SubmitArgs): Promise<boolean> {
  const request = resolveRequest(mutation, handle, friendshipId);
  if (!request) return false;

  try {
    const response = await fetch(request.url, {
      method: request.method,
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: request.body ? JSON.stringify(request.body) : undefined,
      redirect: "manual",
    });
    // A 2xx, or the opaque 3xx redirect_back the controllers issue, both mean the
    // server processed the mutation.
    return response.ok || response.type === "opaqueredirect" || response.status === 0;
  } catch {
    return false;
  }
}

function resolveRequest(mutation: FriendshipMutation, handle: string, friendshipId: number | null): Request | null {
  switch (mutation) {
    case "create":
      return { url: friendshipsPath(), method: "POST", body: { handle } };
    case "accept":
      return friendshipId ? { url: friendshipAcceptancePath(friendshipId), method: "POST" } : null;
    case "decline":
      return friendshipId ? { url: friendshipDeclinePath(friendshipId), method: "POST" } : null;
    case "unfriend":
      return friendshipId ? { url: friendshipPath(friendshipId), method: "DELETE" } : null;
  }
}
